import React, { useState, useEffect, useRef } from "react";
import { Input } from "reactstrap";
import { format } from "date-fns";

const CustomTimePicker = ({
  value,
  onChange,
  onBlur,
  className,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const hourRef = useRef(null);
  const minRef = useRef(null);
  const ampmRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        if (isOpen) {
          setIsOpen(false);
          if (onBlur) {
            onBlur();
          }
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourRef.current)
          hourRef.current.scrollIntoView({ block: "start", behavior: "auto" });
        if (minRef.current)
          minRef.current.scrollIntoView({ block: "start", behavior: "auto" });
        if (ampmRef.current)
          ampmRef.current.scrollIntoView({ block: "start", behavior: "auto" });
      }, 10);
    }
  }, [isOpen]);

  const selectedDate = value || new Date(1970, 0, 1, 1, 0);

  const selectedHour = selectedDate.getHours();
  const selectedMinute = selectedDate.getMinutes();

  const baseHours = Array.from({ length: 12 }, (_, index) => index + 1);
  const baseMinutes = Array.from({ length: 60 }, (_, index) => index);

  const hours = [...baseHours, ...baseHours, ...baseHours];
  const minutes = [...baseMinutes, ...baseMinutes, ...baseMinutes];

  const get24Hour = (hour, period) => {
    if (period === "AM") {
      return hour === 12 ? 0 : hour;
    }

    return hour === 12 ? 12 : hour + 12;
  };

  const currentPeriod = selectedHour >= 12 ? "PM" : "AM";
  const current12Hour = selectedHour % 12 || 12;

  const periods = currentPeriod === "PM" ? ["PM", "AM"] : ["AM", "PM"];

  const handleHourScroll = (e) => {
    const target = e.target;
    if (target.scrollTop < 36) {
      target.scrollTop += 432;
    } else if (target.scrollTop > 864) {
      target.scrollTop -= 432;
    }
  };

  const handleMinScroll = (e) => {
    const target = e.target;
    if (target.scrollTop < 36) {
      target.scrollTop += 2160;
    } else if (target.scrollTop > 4320) {
      target.scrollTop -= 2160;
    }
  };

  const updateTime = (hour, minute, period) => {
    const updatedDate = new Date(selectedDate);
    updatedDate.setHours(get24Hour(hour, period), minute, 0, 0);
    onChange(updatedDate);
  };

  const displayValue = value ? format(value, "hh:mm a") : "";

  return (
    <div className="w-100 custom-time-wrapper" ref={containerRef}>
      <Input
        type="text"
        readOnly
        value={displayValue}
        placeholder={placeholder || "Select time"}
        onClick={() => setIsOpen(!isOpen)}
        className={`${className} custom-time-input`}
      />
      <i className="fa fa-clock text-muted custom-time-icon" />

      {isOpen && (
        <div className="custom-time-picker">
          <div className="custom-time-column">
            <div className="custom-time-column-title">Hour</div>

            <div className="custom-time-scroll" onScroll={handleHourScroll}>
              {hours.map((hour, index) => {
                const active = hour === current12Hour;
                const isMiddle = index >= 12 && index < 24;

                return (
                  <button
                    type="button"
                    key={`hour-${index}`}
                    ref={active && isMiddle ? hourRef : null}
                    className={`custom-time-option ${active ? "active" : ""}`}
                    onClick={() =>
                      updateTime(hour, selectedMinute, currentPeriod)
                    }
                  >
                    {String(hour).padStart(2, "0")}
                  </button>
                );
              })}
              <div className="custom-time-spacer" />
            </div>
          </div>

          <div className="custom-time-column">
            <div className="custom-time-column-title">Minute</div>

            <div className="custom-time-scroll" onScroll={handleMinScroll}>
              {minutes.map((minute, index) => {
                const active = minute === selectedMinute;
                const isMiddle = index >= 60 && index < 120;

                return (
                  <button
                    type="button"
                    key={`minute-${index}`}
                    ref={active && isMiddle ? minRef : null}
                    className={`custom-time-option ${active ? "active" : ""}`}
                    onClick={() =>
                      updateTime(current12Hour, minute, currentPeriod)
                    }
                  >
                    {String(minute).padStart(2, "0")}
                  </button>
                );
              })}
              <div className="custom-time-spacer" />
            </div>
          </div>

          <div className="custom-time-column">
            <div className="custom-time-column-title">AM/PM</div>

            <div className="custom-time-scroll">
              {periods.map((period) => {
                const active = period === currentPeriod;

                return (
                  <button
                    type="button"
                    key={period}
                    ref={active ? ampmRef : null}
                    className={`custom-time-option ${active ? "active" : ""}`}
                    onClick={() =>
                      updateTime(current12Hour, selectedMinute, period)
                    }
                  >
                    {period}
                  </button>
                );
              })}
              <div className="custom-time-spacer" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTimePicker;
